/* Toasts: avisos breves que confirman una acción al instante (snippets
   toast-region y toast; CSS en components.css · Toast).

   API, la misma para cualquier módulo, que no necesita conocer este archivo:

     document.dispatchEvent(new CustomEvent('lny:toast', {
       detail: { message, detail, type, action: { label, url } }
     }));

     message  el aviso, corto («Producto añadido»). Obligatorio.
     detail   segunda línea opcional (el nombre del producto).
     type     'success' (por defecto) | 'error'.
     action   enlace opcional: { label, url }.

   Los textos llegan ya traducidos: aquí no hay ninguno. Todo se escribe con
   `textContent`, nunca como HTML: un mensaje puede traer el nombre de un
   producto o un error del servidor.

   Se apilan (el más nuevo abajo), como mucho MAX a la vez, y se retiran solos:
   `--toast-duration` y `--toast-duration-error` (css-variables). El reloj se
   para con el ratón encima o con el foco dentro, y sigue al salir. */
(() => {
  const region = document.querySelector('[data-toast-region]');
  if (!region) return;

  const MAX = 3;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canPop = typeof region.showPopover === 'function';
  const live = new Map(); // toast → { remaining, started, timer }
  let paused = false;

  const duration = (type) => {
    const token = type === 'error' ? '--toast-duration-error' : '--toast-duration';
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(token));
    return Number.isFinite(value) && value > 0 ? value : 5000;
  };

  /* La región vive en la capa superior (popover). Con un <dialog> modal
     abierto se muda DENTRO de él: fuera, el navegador la deja inerte y debajo
     del velo. Al mudarla el popover se cierra solo, así que se vuelve a abrir
     y queda la última de la capa, por encima del diálogo. */
  const place = () => {
    let modal = null;
    try {
      modal = document.querySelector('dialog[open]:modal');
    } catch (error) {
      /* Un navegador sin `:modal`: se queda en el <body> */
    }
    const host = modal || document.body;
    if (region.parentNode !== host) host.appendChild(region);
    if (!canPop) return;
    try {
      if (!region.matches(':popover-open')) region.showPopover();
    } catch (error) {
      /* Sin popover se queda como una caja fija normal (z-index del CSS) */
    }
  };

  const remove = (toast) => {
    const state = live.get(toast);
    if (!state) return;
    window.clearTimeout(state.timer);
    live.delete(toast);

    const drop = () => toast.remove();
    if (reduced.matches || !toast.classList.contains('is-visible')) {
      drop();
      return;
    }
    toast.classList.remove('is-visible');
    toast.addEventListener('transitionend', drop, { once: true });
    window.setTimeout(drop, 600);
  };

  const arm = (toast) => {
    const state = live.get(toast);
    if (!state || paused) return;
    state.started = Date.now();
    state.timer = window.setTimeout(() => remove(toast), state.remaining);
  };

  const pause = () => {
    if (paused) return;
    paused = true;
    live.forEach((state) => {
      window.clearTimeout(state.timer);
      state.remaining = Math.max(state.remaining - (Date.now() - state.started), 1000);
    });
  };

  const resume = () => {
    if (!paused) return;
    // Sigue parado mientras quede el ratón encima o el foco dentro
    if (region.matches(':hover') || region.contains(document.activeElement)) return;
    paused = false;
    live.forEach((state, toast) => arm(toast));
  };

  const show = (options) => {
    const message = options && options.message ? String(options.message) : '';
    if (!message) return;
    const type = options.type === 'error' ? 'error' : 'success';
    const template = region.querySelector(`template[data-toast-template="${type}"]`);
    if (!template) return;

    const toast = template.content.querySelector('[data-toast]').cloneNode(true);
    toast.querySelector('[data-toast-message]').textContent = message;

    const detail = toast.querySelector('[data-toast-detail]');
    detail.textContent = options.detail ? String(options.detail) : '';
    detail.hidden = !options.detail;

    const action = toast.querySelector('[data-toast-action]');
    if (options.action && options.action.label && options.action.url) {
      action.textContent = String(options.action.label);
      action.setAttribute('href', String(options.action.url));
      action.hidden = false;
    } else {
      action.remove();
    }

    // Un error interrumpe; una confirmación espera su turno (la región es polite)
    if (type === 'error') toast.setAttribute('role', 'alert');

    place();
    // El más antiguo cede el sitio
    Array.from(live.keys())
      .slice(0, Math.max(live.size - MAX + 1, 0))
      .forEach(remove);

    region.appendChild(toast);
    live.set(toast, { remaining: duration(type), started: 0, timer: 0 });
    // Forzar un cálculo de estilo: sin él no hay «antes» y la entrada no anima
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    arm(toast);
  };

  document.addEventListener('lny:toast', (event) => show(event.detail));

  region.addEventListener('click', (event) => {
    const close = event.target.closest('[data-toast-close]');
    if (close) remove(close.closest('[data-toast]'));
  });
  region.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const toast = event.target.closest('[data-toast]');
    if (!toast) return;
    // Dentro de un diálogo, Esc es suyo (lo cierra): aquí no se toca
    if (region.closest('dialog')) return;
    remove(toast);
  });

  region.addEventListener('pointerenter', pause);
  region.addEventListener('pointerleave', resume);
  region.addEventListener('focusin', pause);
  region.addEventListener('focusout', () => window.setTimeout(resume, 0));

  /* Al cerrarse un diálogo, la región vuelve al <body>: dentro de un diálogo
     cerrado no se ve. `close` no burbujea, pero sí se captura. */
  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement && event.target.contains(region)) place();
    },
    true
  );

  /* Abierta desde el principio, aunque vacía: una región viva que ya está
     pintada cuando llega el aviso es la que los lectores de pantalla leen. */
  place();
})();
